/**
 * FeatureGate (#67e P2) — renders its children only when a catalogued feature is
 * built into this deployment (`isFeatureBuilt`, resolving build-time VITE_FEATURES).
 * When a feature is de-selected, its route renders a clean "not enabled" notice
 * instead of the page — and the lazy chunk isn't even loaded.
 *
 * Wrap the ROUTE ELEMENT (not the path) so route registration / nav-coverage are
 * unaffected: `<Route path="roi" element={<FeatureGate feature="ppm.roi"><RoiModule/></FeatureGate>} />`.
 * Hiding the matching nav item is a separate slice (Sidebar).
 */
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, ArrowLeft } from 'lucide-react'
import { isFeatureBuilt } from '@/config/featureCatalog'

export function FeatureGate({ feature, children }: { feature: string; children: ReactNode }) {
  if (isFeatureBuilt(feature)) return <>{children}</>
  return <FeatureNotEnabled />
}

/**
 * Exported for use as a module catch-all route element: a gated feature whose
 * route isn't mounted at all (the 21 unmounted advanced timesheet routes)
 * previously rendered a BLANK content shell — no notice, no heading. A
 * `<Route path="*">` with this element gives those paths (and typos) the same
 * clean notice as a mounted-but-gated route.
 */
export function FeatureNotEnabled() {
  const navigate = useNavigate()
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-muted text-text-secondary">
        <Lock size={26} />
      </div>
      <h1 className="mt-4 text-heading-md font-display text-text-primary">Feature not enabled</h1>
      <p className="mt-2 max-w-md text-body-sm text-text-secondary">
        This feature isn't part of this deployment's build. Enable it by rebuilding the fork with it
        selected in Client Setup (it controls the{' '}
        <code className="px-1 py-0.5 rounded bg-surface-muted text-[12px]">VITE_FEATURES</code> build config).
      </p>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-text-secondary shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-800 active:bg-indigo-100"
      >
        <ArrowLeft size={15} />
        Go back
      </button>
    </div>
  )
}
